import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@quintoandar-tokko/jopijs';
import PendingCard from '../PendingsCard';
import DashboardEmptyStateCard from '../../DashboardEmptyStateCard';
import MarketplaceRegistrationQualityRow from './MarketplaceRegistrationQualityRow';
import { MARKETPLACE_QUALITY_VALUES, PROPERTIES_STATUS } from '../../constants';
import { getPropertiesSearchParams } from '../utils';
import {
  formatMarketplaceQualityCount,
  getMarketplaceQualityTotal,
  isMarketplaceQualityEmpty,
  mapMarketplaceQualityLevels,
} from './utils';
import { getMarketplaceRegistrationQuality } from '@/store/slices/components/dashboard/service';
import useFetchDashboard from '../../customHooks/useFetchDashboard';
import { useTrackPendingTab } from '../../customHooks/useTrackDashboard';
import Trans from '@/components/shared/Trans';
import translations from '../../Dashboard.trans.json';
import PropertyIcon from '@/public/images/dashboard/property.svg';
import styles from './marketplaceRegistrationQuality.module.scss';

function MarketplaceRegistrationQuality() {
  const dispatch = useDispatch();
  const track = useTrackPendingTab();
  const {
    loading,
    hasFetched,
    empty,
    error,
    enabled,
    count,
    levels,
    redirectUrl,
  } = useSelector(
    (state) => state.DashboardSlice.marketplaceRegistrationQuality
  );
  const { selectedIds } = useSelector((state) => state.FilterBarSlice.filters);
  const { data: userData } = useSelector((state) => state.UserSlice);
  const { ref, isStale, useFetchOnViewport } = useFetchDashboard();

  const mappedLevels = mapMarketplaceQualityLevels(levels);
  const total = getMarketplaceQualityTotal(count, mappedLevels);
  const showEmpty =
    error || empty || !enabled || isMarketplaceQualityEmpty(mappedLevels);

  useFetchOnViewport(
    (query) => {
      dispatch(getMarketplaceRegistrationQuality({ query }));
    },
    [],
    { hasFetched }
  );

  const handleRowClick = (level) => {
    if (!level?.url) return;
    window.open(level.url, '_blank', 'noopener,noreferrer');
  };

  const tokkoBrokerHost = localStorage.getItem('tokkoBrokerHost');
  const fallbackRedirectUrl = `${tokkoBrokerHost}/properties/?search_options=${getPropertiesSearchParams(
    {
      statusId: PROPERTIES_STATUS.AVAILABLE,
      selectedIds,
      quality: MARKETPLACE_QUALITY_VALUES,
    }
  )}`;
  const headerRedirectUrl = showEmpty
    ? null
    : redirectUrl || fallbackRedirectUrl;

  return (
    <PendingCard ref={ref} loading={loading || isStale}>
      <PendingCard.Header
        redirectUrl={headerRedirectUrl}
        toolTipText={Trans(translations, 'ShowAll')}
        infoTooltipText={Trans(
          translations,
          'MarketplaceRegistrationQualityTooltip'
        )}
        trackCallback={(element) =>
          track.marketplaceRegistrationQuality(element)
        }
      >
        {Trans(translations, 'MarketplaceRegistrationQuality')}
      </PendingCard.Header>
      {showEmpty ? (
        <DashboardEmptyStateCard
          icon={PropertyIcon}
          title={Trans(
            translations,
            'HereYouWillSeeMarketplaceRegistrationQuality'
          )}
        />
      ) : (
        <>
          <Box className={styles.totalRow}>
            <Text className={styles.total}>
              {formatMarketplaceQualityCount(total, userData?.user_language)}
            </Text>
          </Box>
          <Box className={styles.list}>
            {mappedLevels.map((level, index) => (
              <Box key={level.id}>
                <MarketplaceRegistrationQualityRow
                  level={level}
                  locale={userData?.user_language}
                  onClick={handleRowClick}
                  trackCallback={(element) =>
                    track.marketplaceRegistrationQuality(element)
                  }
                />
                {index < mappedLevels.length - 1 && (
                  <Box className={styles.separator} />
                )}
              </Box>
            ))}
          </Box>
        </>
      )}
    </PendingCard>
  );
}

export default MarketplaceRegistrationQuality;
